import Keycloak from "keycloak-js";
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";

interface AuthState {
  ready: boolean;
  authenticated: boolean;
  token?: string;
  username?: string;
  roles: string[];
  login: () => Promise<void>;
  register: () => Promise<void>;
  logout: () => Promise<void>;
  account: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL ?? "http://127.0.0.1:11002",
  realm: import.meta.env.VITE_KEYCLOAK_REALM ?? "jewelry-store",
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "atelier-web",
});
let keycloakInitialization: Promise<boolean> | null = null;

function currentPageUrl(): string {
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
}

function initializeKeycloak(): Promise<boolean> {
  if (!keycloakInitialization) {
    keycloakInitialization = keycloak
      .init({
        onLoad: "check-sso",
        pkceMethod: "S256",
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: `${window.location.origin}/app/silent-check-sso.html`,
      })
      .catch(() => false);
  }
  return keycloakInitialization;
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [ready, setReady] = useState(false);
  const [, setRevision] = useState(0);

  useEffect(() => {
    let active = true;
    void initializeKeycloak().then(() => {
      if (active) setReady(true);
    });
    const timer = window.setInterval(() => {
      if (!keycloak.authenticated) return;
      void keycloak.updateToken(45).then((refreshed) => {
        if (refreshed && active) setRevision((value) => value + 1);
      });
    }, 30_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  const roles = keycloak.realmAccess?.roles ?? [];
  const value: AuthState = {
    ready,
    authenticated: Boolean(keycloak.authenticated),
    token: keycloak.token,
    username:
      typeof keycloak.tokenParsed?.preferred_username === "string"
        ? keycloak.tokenParsed.preferred_username
        : undefined,
    roles,
    login: async () => {
      await keycloak.login({ redirectUri: currentPageUrl() });
    },
    register: async () => {
      await keycloak.register({ redirectUri: currentPageUrl() });
    },
    logout: async () => {
      await keycloak.logout({ redirectUri: `${window.location.origin}/` });
    },
    account: async () => {
      await keycloak.accountManagement();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
